/**
 * AssistantModeChooser — bilingual one-screen channel picker shown when
 * a bot has more than one widget channel enabled (voice + chat).
 *
 * Plan reference: PR 4 — widget shell + chooser.
 *
 * NOTE — focus management, focus trap, Escape handling, and outside-click
 * dismissal are owned by the parent `AssistantPanelFrame`. This component
 * only renders the picker content.
 */

import React from 'react';
import { CHANNEL_CHOOSER_LABELS, type LanguageCode } from '../config/language';
import type { WidgetChannel } from '../types';

interface AssistantModeChooserProps {
  availableChannels: WidgetChannel[];
  onSelect: (channel: WidgetChannel) => void;
  language: LanguageCode;
  primaryColor: string;
  onClose?: () => void;
}

const RTL_LANGUAGES: LanguageCode[] = ['he', 'ar'];

export const AssistantModeChooser: React.FC<AssistantModeChooserProps> = ({
  availableChannels,
  onSelect,
  language,
  primaryColor,
  onClose,
}) => {
  const labels = CHANNEL_CHOOSER_LABELS[language] ?? CHANNEL_CHOOSER_LABELS.en;
  const isRtl = RTL_LANGUAGES.includes(language);

  const renderChannel = (channel: WidgetChannel) => {
    const isVoice = channel === 'voice';
    const title = isVoice ? labels.voice : labels.chat;
    const description = isVoice ? labels.voiceDesc : labels.chatDesc;
    return (
      <button
        key={channel}
        type="button"
        onClick={() => onSelect(channel)}
        aria-label={`${title} — ${description}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '24px 16px',
          minHeight: 160,
          flex: 1,
          border: `2px solid ${primaryColor}`,
          borderRadius: 16,
          background: 'transparent',
          color: '#1f2937',
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'transform 0.15s ease, background-color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = `${primaryColor}14`;
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 40, lineHeight: 1 }}>
          {isVoice ? '🎙️' : '💬'}
        </span>
        <span style={{ fontSize: 18, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 13, color: '#6b7280', textAlign: 'center' }}>{description}</span>
      </button>
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="iland-channel-chooser-title"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '24px 20px',
        background: '#ffffff',
        gap: 20,
        boxSizing: 'border-box',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h2
          id="iland-channel-chooser-title"
          style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}
        >
          {labels.title}
        </h2>
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>{labels.subtitle}</p>
      </header>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 16,
          flex: 1,
          alignItems: 'stretch',
        }}
      >
        {availableChannels.map(renderChannel)}
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={labels.close}
          title={labels.close}
          style={{
            position: 'absolute',
            top: 12,
            ...(isRtl ? { left: 12 } : { right: 12 }),
            width: 32,
            height: 32,
            borderRadius: 16,
            border: 'none',
            background: '#f3f4f6',
            color: '#374151',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

export default AssistantModeChooser;
